import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  getFilesById,
  deleteFile,
  dispatchFiles,
  getOptionFile,
  updateFiles,
  updateStatusTransition,
  onHoldFile
} from 'services/files';
import { useAsyncCallback } from 'hooks/useAsyncCallback';
import { ClientOption, CreateFiles } from 'app/pages/Admin/FilesPage/components/FileForm/hook';
import { useFlash } from 'contexts/flash';
import { useNavigate, useParams } from 'react-router-dom';
import { NewLeadForm } from 'app/components/modules/ModalNewLead';
import { compact, omit } from 'lodash';
import { Lead, TFile, TypeLead } from 'types/user';
import { AddNote } from 'app/pages/FileDetail/components/LeadBox';
import { createLead, CreateLeadPayload, deleteLead, updateLead } from 'services/leads';
import { TypeOption } from 'app/pages/Admin/NewFileSetUpPage/hook';

export const useInjection = () => {
  const { setFlash } = useFlash();
  const navigate = useNavigate();
  const { id } = useParams();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [file, setFile] = useState<TFile>();
  const [showModalDeleteFile, setShowModalDeleteFile] = useState<boolean>(false);
  const [showModalDeleteLead, setShowModalDeleteLead] = useState<boolean>(false);
  const [idLeadDelete, setIdLeadDelete] = useState<number>();
  const [optionClient, setOptionClient] = useState<ClientOption[]>([]);
  const [optionClientChild, setOptionClientChild] = useState<ClientOption[]>([]);
  const [optionAuctionDeliveryLocation, setOptionAuctionDeliveryLocation] = useState<TypeOption[]>(
    []
  );

  const getInitFile = useCallback(async () => {
    const [dataFile, dataOption] = await Promise.all([getFilesById(Number(id)), getOptionFile()]);
    const { resOptionClient, resOptionAuctionDeliveryLocation } = dataOption;
    setOptionClient(resOptionClient.clientOptions);
    setOptionAuctionDeliveryLocation(
      resOptionAuctionDeliveryLocation.auctionHouses?.map(
        (item: { id: number; value: string }) => ({
          value: item.id,
          label: item.value
        })
      )
    );
    const fileRes = dataFile.file as TFile;
    const hasClientChild = fileRes?.clientOption?.parentId;
    if (hasClientChild) {
      const listChild = resOptionClient.clientOptions?.find(
        (item: { id: number; value: string; parentId: number }) =>
          item.id === fileRes?.clientOption?.parentId
      )?.children;
      setOptionClientChild(listChild);
    }
    const userAssign = fileRes.dispatchFiles?.map((item) => ({
      value: `${item.user.id}`,
      label: `${item.user.firstName} ${item.user.lastName} ( ${item.user.email} )`
    }));

    const fileConvert = {
      ...fileRes,
      userAssign,
      clientOptionsId: fileRes?.clientOption?.parentId
        ? fileRes?.clientOption?.parentId
        : fileRes?.clientOption?.id,
      clientOptionsChildId: fileRes?.clientOption?.parentId ? fileRes?.clientOption?.id : undefined
    };
    setFile(fileConvert);
    setLeads(fileRes.leads);
  }, [id]);

  const changeOptionClient = useCallback(
    (value: number) => {
      const listChild = optionClient.find((item) => item.id === value)?.children;
      if (listChild?.length) {
        setOptionClientChild(listChild);
        return;
      }
      setOptionClientChild([]);
    },
    [optionClient]
  );
  const handleUpdateFile = useCallback(
    async ({
      value
    }: {
      value: CreateFiles;
      filePhotos?: { file: File; url: string }[];
      fileDocument?: { file: File; url: string }[];
    }) => {
      const payload = {
        ...omit(value, ['fileDocuments', 'photos', 'leads', 'id']),
        clientOptionsId: value.clientOptionsChildId || value.clientOptionsId
      };
      const userIds = compact(payload.userAssign?.map((item) => Number(item.value))) || [];
      await Promise.all([
        updateFiles(Number(id), payload),
        dispatchFiles({ fileId: Number(id), userIds })
      ]);
      await getInitFile();
      setFlash({ type: 'success', message: 'Update files successful' });
      if (!userIds.length) {
        navigate('/admin/file-overview');
      }
    },
    [getInitFile, id, navigate, setFlash]
  );

  const handleAddNewLead = useCallback(
    async (value: NewLeadForm) => {
      let earlyMorning = false;
      let doorKnock = false;
      if (value.type === TypeLead.POR || value.type === TypeLead.MVS) {
        earlyMorning = true;
        doorKnock = true;
      }
      const leadTowYards = value.towYards?.map((item) => ({ content: item.value })) || [];
      const payload = {
        ...omit(value, ['type']),
        earlyMorning,
        doorKnock,
        leadTowYards,
        type: value.type,
        fileId: Number(id),
        notes:
          value?.noteAdmin && value.noteAdmin.trim() !== ''
            ? [{ content: value.noteAdmin }]
            : undefined
      };
      await createLead(payload);
      await getInitFile();
      setFlash({ type: 'success', message: 'Create leads successful' });
    },
    [getInitFile, id, setFlash]
  );

  const handleDeleteLead = useCallback(async () => {
    if (!idLeadDelete) return;
    await deleteLead(idLeadDelete);
    await getInitFile();
    setShowModalDeleteLead(false);
    setFlash({ type: 'success', message: 'Delete note leads successful' });
  }, [getInitFile, idLeadDelete, setFlash]);

  const handleDeleteFile = useCallback(async () => {
    if (!id) return;
    await deleteFile(Number(id));
    setShowModalDeleteFile(false);
    setFlash({ type: 'success', message: 'Delete file successful' });
    navigate('/admin/file');
  }, [id]);

  const handleCloseNotFound = useCallback(async () => {
    if (!id) return;
    await updateStatusTransition(Number(id), 'CLOSE_NOT_FOUND');
    setFlash({ type: 'success', message: 'Close not found successful' });
    navigate('/admin/file');
  }, [id]);

  const handleCloseClient = useCallback(async () => {
    if (!id) return;
    await updateStatusTransition(Number(id), 'CLOSE_CLIENT_REQUEST');
    setFlash({ type: 'success', message: 'Close client successful' });
    navigate('/admin/file');
  }, [id]);

  const handleUpdateLead = useCallback(
    async ({
      idLead,
      payload,
      message
    }: {
      idLead: number;
      payload: Partial<CreateLeadPayload>;
      message: string;
    }) => {
      await updateLead(idLead, { ...payload, fileId: Number(id) });
      setFlash({ type: 'success', message });
      await getInitFile();
    },
    [id, setFlash, getInitFile]
  );

  const { asyncCallback: asyncUpdateLead } = useAsyncCallback(handleUpdateLead, [id]);

  const { asyncCallback: asyncDeleteFile } = useAsyncCallback(handleDeleteFile, []);
  const { asyncCallback: asyncGetInitFiles } = useAsyncCallback(getInitFile, []);
  const { asyncCallback: asyncUpdateFile } = useAsyncCallback(handleUpdateFile, []);
  const { asyncCallback: asyncCreateLeads } = useAsyncCallback(handleAddNewLead, []);
  const { asyncCallback: asyncHandleDeleteLead } = useAsyncCallback(handleDeleteLead, []);
  const { asyncCallback: asyncCloseNotFound } = useAsyncCallback(handleCloseNotFound, []);
  const { asyncCallback: asyncCloseClient } = useAsyncCallback(handleCloseClient, []);

  const handleOnHoldFile = useCallback(async () => {
    if (!file?.id) return;
    const isOnHold = !file?.onHold;
    await onHoldFile(Number(file?.id), isOnHold);
    setFlash({
      type: 'success',
      message: isOnHold ? 'File put on hold successfully' : 'File activated successfully'
    });
    navigate('/admin/file-overview');
  }, [file, navigate, setFlash]);

  const { asyncCallback: asyncHandleOnHoldFile } = useAsyncCallback(handleOnHoldFile, [
    handleOnHoldFile
  ]);

  useEffect(() => {
    asyncGetInitFiles();
  }, [id]);

  const handleToggleRemind = useCallback(
    async ({ idLead, remind }: { idLead: number; remind: boolean }) => {
      await handleUpdateLead({
        idLead,
        payload: { flag: remind },
        message: 'Updated successful'
      });
    },
    [handleUpdateLead]
  );

  return {
    asyncUpdateFile,
    reloadPage: getInitFile,
    leads,
    setLeads,
    handleAddNewLead: asyncCreateLeads,
    handleDeleteLead: asyncHandleDeleteLead,
    handleDeleteFile: asyncDeleteFile,
    handleCloseNotFound: asyncCloseNotFound,
    handleCloseClient: asyncCloseClient,
    file,
    showModalDeleteFile,
    setShowModalDeleteFile,
    showModalDeleteLead,
    setShowModalDeleteLead,
    setIdLeadDelete,
    optionClient,
    optionClientChild,
    optionAuctionDeliveryLocation,
    changeOptionClient,
    handleAddNewNote: async ({ idLead, textNote }: AddNote) =>
      await asyncUpdateLead({
        idLead,
        payload: {
          notes: [{ content: textNote }]
        },
        message: 'Add note leads successful'
      }),
    handleEditNote: async (
      idLead: number,
      idNote: number,
      content: string,
      notePhotosIds: number[]
    ) => {
      await asyncUpdateLead({
        idLead,
        payload: {
          notes: [{ id: idNote, content: content, notePhotosIds }]
        },
        message: 'Edit note leads successful'
      });
    },
    handleChooseRuleOut: async (idLead: number, ruleOut: boolean) => {
      await asyncUpdateLead({
        idLead,
        payload: { ruleOut },
        message: 'Updated successful'
      });
    },
    handleRemindLead: async (idLead: number, remind: boolean) => {
      await handleToggleRemind({
        idLead,
        remind
      });
    },
    handleAddTowYards: async ({
      idLead,
      towYards
    }: {
      idLead: number;
      towYards: { value: string; id?: number; checked: boolean }[];
    }) => {
      const leadTowYards =
        compact(
          towYards?.map((item) => {
            if (!item.id) {
              return { content: item.value, checked: item.checked };
            }
            return { id: item.id, content: item.value, checked: item.checked };
          })
        ) || [];
      if (!leadTowYards.length) return;
      await asyncUpdateLead({
        idLead,
        payload: {
          leadTowYards
        },
        message: 'Add tow yards successful'
      });
    },
    handleDeleteTowYards: async ({
      idTowYards,
      idLead
    }: {
      idLead: number;
      idTowYards: number;
    }) => {
      await asyncUpdateLead({
        idLead,
        payload: {
          leadTowYards: [
            {
              id: idTowYards,
              isDelete: true
            }
          ]
        },
        message: 'Add tow yards successful'
      });
    },
    handleChangeVinStatus: async ({ vinStatus, idLead }: { idLead: number; vinStatus: string }) => {
      await asyncUpdateLead({
        idLead,
        payload: {
          vinStatus
        },
        message: 'update VIN check successful'
      });
    },
    handleUploadFileCarfax: async ({
      files,
      idLead,
      oldDocIds
    }: {
      idLead: number;
      files: File[];
      oldDocIds: number[];
    }) => {
      const formData = new FormData();
      let leadDocumentIds: number[] = [];
      if (files?.length) {
        for (let x = 0; x < files.length; x++) {
          formData.append('files', files[x], files[x].name);
        }
        const { data: resListIdFile } = await axios.post('/storages/lead-document', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        leadDocumentIds = [...resListIdFile.files, ...oldDocIds];
        await asyncUpdateLead({
          idLead: idLead,
          payload: {
            leadDocumentIds
          },
          message: 'Update leads successful'
        });
      }
    },
    handleDeleteNoteLead: async (idNote: number, idLead: number) =>
      await asyncUpdateLead({
        idLead,
        payload: {
          notes: [{ id: idNote, isDelete: true }]
        },
        message: 'Delete note leads successful'
      }),
    handleDeleteDocumentLead: async ({
      idLead,
      oldDocIds
    }: {
      idLead: number;
      oldDocIds: number[];
    }) => {
      await asyncUpdateLead({
        idLead: idLead,
        payload: {
          leadDocumentIds: [...oldDocIds]
        },
        message: 'Update leads successful'
      });
    },
    handleOnHoldFile: asyncHandleOnHoldFile
  };
};
